import { formatNumber } from '@angular/common';
import { Component, EventEmitter, Input, NgZone, Output } from '@angular/core';
import { FormControl } from '@ngneat/reactive-forms';
import { map } from 'rxjs/operators';
import { ConfirmDialogService } from '../../services/confirm-dialog-service/confirm-dialog.service';
import { FileService, FileStorage } from '../../services/file.service';

@Component({
    selector: 'app-codalt-upload-file',
    templateUrl: './codalt-upload-file.component.html',
    styleUrls: ['./codalt-upload-file.component.scss'],
    standalone: false,
})
export class CodaltUploadFileComponent {
    @Input() fileStorage = FileStorage.ToolBox;
    @Input() fcFile: FormControl<string>;
    @Input() allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/svg+xml',
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv',
        'text/plain',
    ];
    @Input() label: string;
    @Input() publicUpload = false;

    @Output() submitted = new EventEmitter<File>();
    @Output() onError = new EventEmitter<void>();

    loading = false;

    constructor(
        private confirmDialogService: ConfirmDialogService,
        private ngZone: NgZone,
        private fileService: FileService
    ) {}

    submit(event) {
        event.stopPropagation();
        event.preventDefault();

        const file = event.srcElement.files[0];

        if (file) {
            if (this.uploadFile(file)) {
                this.submitted.emit(file);
            }
        }
    }

    uploadFile(file: File): boolean {
        this.loading = true;
        const maxSizeMB = 90;
        if (file.size > maxSizeMB * 1024 * 1024) {
            this.confirmDialogService
                .confirm({
                    title: 'Bestand te groot',
                    text: `Dit bestand is groter dan ${formatNumber(maxSizeMB, 'nl')} MB`,
                    confirm: 'Oké',
                    cancel: null,
                })
                .then(() => {
                    this.loading = false;
                });
            return false;
        } else if (
            this.allowedTypes.includes(file.type) ||
            (!file.type && this.allowedTypes.some(at => file.name.endsWith(at)))
        ) {
            const upload$ = this.publicUpload
                ? this.fileService.resizeAndUploadPublic(file).pipe(map(response => response.file))
                : file.type.startsWith('image/')
                  ? this.fileService.resizeAndUpload(file, this.fileStorage).pipe(map(response => response.file))
                  : this.fileService.upload(file, this.fileStorage).pipe(map(response => response.data.file));

            upload$.subscribe({
                next: fileName => {
                    this.ngZone.run(() => {
                        this.fcFile.setValue(fileName);
                    });
                },
                error: err => {
                    console.error('Error uploading file:', err.data.message);

                    this.onError.emit();

                    this.confirmDialogService
                        .confirm({
                            title: 'Upload niet gelukt',
                            text: err.data.message,
                            confirm: 'Oké',
                            cancel: null,
                        })
                        .then(() => {
                            this.loading = false;
                        });
                },
                complete: () => {
                    this.loading = false;
                },
            });
            return true;
        } else {
            this.confirmDialogService
                .confirm({
                    title: 'Verkeerd bestand',
                    text: 'Dit bestandsformaat wordt niet ondersteund',
                    confirm: 'Oké',
                    cancel: null,
                })
                .then(() => {
                    this.loading = false;
                });
        }
        return false;
    }
}
